import { networkError } from './errorMessages'
import { isObservableArray } from 'mobx'
import { toast, Zoom } from 'react-toastify'
import { getRawMessage } from 'locale'

export const isObject = (obj) =>
  obj != null && obj.constructor.name === 'Object'

export const isEmptyObject = (obj) => {
  if (typeof obj !== 'object') return false

  for (let _ in obj) {
    return false
  }

  return true
}

export const isEmptyArray = (thing) =>
  Array.isArray(thing) && thing.length === 0

export const isEmpty = (obj) =>
  obj === '' ||
  obj === null ||
  obj === undefined ||
  isEmptyObject(obj) ||
  isEmptyArray(obj)

export const toPersianDigit = (a) => {
  if (a !== null && a !== undefined) {
    if (typeof a === 'number') {
      a = a.toString()
    }

    return a.replace(/\d+/g, function (digit) {
      var enDigitArr = [],
        peDigitArr = []

      for (let i = 0; i < digit.length; i++) {
        enDigitArr.push(digit.charCodeAt(i))
      }

      for (let j = 0; j < enDigitArr.length; j++) {
        peDigitArr.push(
          String.fromCharCode(enDigitArr[j] + (!!a && a === true ? 1584 : 1728))
        )
      }

      return peDigitArr.join('')
    })
  } else {
    return ''
  }
}

// last one is dot for floating point numbers
var persian = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹', '.']
var english = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.']

const engToPerMapper = (char, acceptDecimal) => {
  if (
    !acceptDecimal &&
    english.indexOf(char) !== -1 &&
    persian.indexOf(char) !== -1
  ) {
    // return null if it is neither persian nor english number
    // because it a alphabetic char
    return null
  } else if (persian.indexOf(char) !== -1) {
    return char
  } else if (english.indexOf(char) !== -1) {
    return persian[english.indexOf(char)]
  }
}

const perToEngMapper = (char, acceptDecimal) => {
  if (
    !acceptDecimal &&
    english.indexOf(char) !== -1 &&
    persian.indexOf(char) !== -1
  ) {
    // return null if it is neither persian nor english number
    // because it a alphabetic char
    return null
  } else if (persian.indexOf(char) !== -1) {
    return english[persian.indexOf(char)]
  } else if (english.indexOf(char) !== -1) {
    return char
  }
}

const numericCharFilter = (char) => {
  if (english.indexOf(char) !== -1 && persian.indexOf(char) !== -1) {
    return false
  } else if (persian.indexOf(char) !== -1) {
    return true
  } else if (english.indexOf(char) !== -1) {
    return true
  }
}

export const ensurePersianNumber = (text, isDecimal) => {
  if (typeof text === 'string') {
    return text
      .split('')
      .map((char) => engToPerMapper(char, isDecimal))
      .filter((char) => !!char)
      .join('')
  } else {
    return ''
  }
}

export const ensureEnglishNumber = (text, isDecimal) => {
  if (typeof text === 'string') {
    return text
      .split('')
      .map((char) => perToEngMapper(char, isDecimal))
      .filter((char) => !!char)
      .join('')
  } else {
    return ''
  }
}

export const filterNonNumericChars = (text) => {
  if (typeof text === 'string') {
    return text.split('').filter(numericCharFilter).join('')
  } else {
    return ''
  }
}

export const reverseString = (string) => string.split('').reverse().join('')

export function replaceLastInstance(string, charToReplace, Replacement) {
  return reverseString(
    reverseString(string).replace(charToReplace, Replacement)
  )
}

export function thousandsSeparator(rowNum) {
  return rowNum?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
export const resOK = (status) => status >= 200 && status < 400

export const The = (promise, options = {}) => {
  options.loadingToggle && options.loadingToggle()
  return promise
    .then(({ data: { status, errors, data } }) => {
      options.loadingToggle && options.loadingToggle()
      if (resOK(status)) {
        return [null, data]
      } else {
        // make sure there are no nested arrays
        errors.general_errors = flattenArray(errors.general_errors || [])
        return [errors, null]
      }
    })
    .catch((err) => {
      options.loadingToggle && options.loadingToggle()
      // in case there is undefined error like error 404 on endpoints
      if (!err) return [{ general_errors: [networkError] }, null]
      // in case the error is empty
      if (!err.general_errors && !err.form_errors)
        return [{ general_errors: [networkError] }, null]
      err.general_errors = flattenArray(err.general_errors || [])
      return [err, null]
    })
}

/**
 * make promise call cancellable
 *
 * (source)[https://github.com/facebook/react/issues/5465#issuecomment-157888325]
 *
 * @param {Promise} promise
 */
export const makeCancelable = (promise) => {
  let hasCanceled_ = false

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (val) => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
      (error) => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error))
    )
  })

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true
    },
  }
}

export const reducePromises = (...args) => Promise.all(args)

export const createJsonData = (formObject = {}, options = {}) => {
  const blackListValues = [null, undefined, '']
  const alwaysSend = options.alwaysSend || []
  const fieldKeys = Object.keys(formObject) || []
  const emptyFields = fieldKeys.filter(
    (field) =>
      !alwaysSend.includes(field) && blackListValues.includes(formObject[field])
  )
  const allowedFields = omit(emptyFields)(formObject)
  return JSON.stringify(allowedFields)
}

export const createFormData = (formObject, options = {}) => {
  let formData = new FormData()
  appendToFormData(formObject, formData, options)
  return formData
}

function appendToFormData(formObject, formData, options) {
  const filterList = [null, undefined, '']
  const alwaysSend = options.alwaysSend || []

  for (const field in formObject) {
    const fieldValue = formObject[field]

    if (alwaysSend.includes(field) || !filterList.includes(fieldValue)) {
      if (typeof fieldValue === 'object') {
        //it's alwaysSend object that sent null to empty or clear the current value of field like logo
        if (fieldValue === null) {
          formData.append(field, fieldValue)
        } else {
          if (
            typeof fieldValue.name === 'string' &&
            typeof fieldValue.size === 'number'
          ) {
            // it's a file
            formData.append(field, fieldValue)
          } else {
            // it's an object
            formData.append(field, JSON.stringify(fieldValue))
          }
        }
      } else {
        // it's not an object
        formData.append(field, fieldValue)
      }
    }
  }
}

export const flattenArray = (list) =>
  list.reduce((a, b) => a.concat(Array.isArray(b) ? flattenArray(b) : b), [])

function showNotification(message = 'Server Error', type) {
  toast(message, {
    type,
    position: toast.POSITION.TOP_RIGHT,
    transition: Zoom,
    autoClose: 3 * 1000,
    hideProgressBar: true,
    closeOnClick: true,
  })
}

export const notify = (messages, type = 'info') => {
  if (!Array.isArray(messages)) {
    showNotification(messages, type)
    return
  }

  for (const message of messages) {
    showNotification(message, type)
  }
}

export const showSuccessNotification = (messages) => {
  notify(messages, 'success')
}

export const showErrorNotification = (messages) => {
  notify(messages, 'error')
}

export const flattenFormErrors = (fe = {}) =>
  Object.keys(fe)
    .map((fieldName) => ({
      [fieldName]: Array.isArray(fe[fieldName])
        ? fe[fieldName].join(' ')
        : fe[fieldName],
    }))
    .reduce((prev, curr) => ({ ...prev, ...curr }), {})

export const flattenErrors = ({
  general_errors: ge = [],
  form_errors: fe = {},
}) => {
  const formErrors = flattenFormErrors(fe)
  ;[
    ...ge,
    ...Object.keys(formErrors).map(
      (fieldName) => `${fieldName}: ${formErrors[fieldName]}`
    ),
  ].map((err) => notify(err, 'error'))
}

export const setErrorsOnFormAndNotifyGeneralErrors = (err, form) => {
  if (!form) {
    flattenErrors(err)
    return
  }
  if (err.general_errors) {
    notify(flattenArray(err.general_errors), 'error')
  }
  if (err.form_errors) {
    invalidateForm(form, err.form_errors)
  }
}

export const setCalculatorErrors = (errors = {}, formSetError = null) => {
  // server errors = 500
  if (errors.response && errors.response.status >= 500) {
    notify(networkError, 'error')
    return
  }

  if (!formSetError) {
    flattenErrors(errors)
    return
  }
  if (errors.form_errors && formSetError) {
    const fieldsHasError = Object.keys(errors.form_errors)
    fieldsHasError.forEach((field) => {
      let fieldErrors = ''
      errors.form_errors[field].forEach((err) => {
        fieldErrors = `${fieldErrors} - ${err} `
      })
      formSetError(field, { message: fieldErrors })
    })
  }
}
// TO DO: replace calculator error with this new fn
export const setFormHookErrors = (errors = {}, formSetError = null) => {
  // server errors = 500
  if (errors.response && errors.response.status >= 500) {
    notify(networkError, 'error')
    return
  }

  if (!formSetError) {
    flattenErrors(errors)
    return
  }
  if (errors.general_errors) {
    flattenErrors({ general_errors: errors.general_errors })
  }
  if (errors.form_errors) {
    const fieldsHasError = Object.keys(errors.form_errors)
    fieldsHasError.forEach((field) => {
      let fieldErrors = ''
      errors.form_errors[field].forEach((err) => {
        fieldErrors = `${fieldErrors} - ${err} `
      })
      formSetError(field, { message: fieldErrors })
    })
    return
  }
}
/**
 * Unflatten an object with the paths for keys.
 * Use nested Array.prototype.reduce() to convert the flat path to a leaf node.
 * Use String.prototype.split('.') to split each key with a dot delimiter and Array.prototype.reduce()
 * to add objects against the keys.
 * If the current accumulator already contains a value against a particular key, return its value as
 * the next accumulator. Otherwise, add the appropriate key-value pair to the accumulator object and
 * return the value as the accumulator.
 *
 * @summary Unflatten an object with the paths for keys.
 *
 * @param {object} obj
 */
export const unFlattenObject = (obj) =>
  Object.keys(obj).reduce((res, k) => {
    k.split('.').reduce(
      (acc, e, i, keys) =>
        acc[e] ||
        (acc[e] = isNaN(Number(keys[i + 1]))
          ? keys.length - 1 === i
            ? obj[k]
            : {}
          : []),
      res
    )
    return res
  }, {})

/**
 * Flattens an object with the paths for keys.
 * - Use recursion.
 * - Use Object.keys(obj) combined with Array.prototype.reduce() to convert every leaf node to a flattened path node.
 * - If the value of a key is an object, the function calls itself with the appropriate prefix to create
 *   the path using Object.assign().
 * - Otherwise, it adds the appropriate prefixed key-value pair to the accumulator object.
 * - You should always omit the second argument, prefix, unless you want every key to have a prefix.
 *
 * @summary Flattens an object with the paths for keys.
 *
 * @param {object} obj
 */
export const flattenObject = (obj, prefix = '') =>
  Object.keys(obj).reduce((acc, k) => {
    const pre = prefix.length ? `${prefix}.` : ''
    if (
      typeof obj[k] === 'object' &&
      obj[k] !== null &&
      Object.keys(obj[k]).length > 0
    )
      Object.assign(acc, flattenObject(obj[k], pre + k))
    else acc[pre + k] = obj[k]
    return acc
  }, {})

const flattenErrorKeys = (formErrors, prefix = '') =>
  Object.keys(formErrors).reduce((acc, k) => {
    const pre = prefix.length ? `${prefix}.` : ''

    if (
      typeof formErrors[k] === 'object' &&
      formErrors[k] !== null &&
      Object.keys(formErrors[k]).length > 0
    ) {
      if (
        Array.isArray(formErrors[k]) &&
        formErrors[k].every((i) => typeof i === 'string')
      ) {
        acc[pre + k] = formErrors[k].join(' - ')
      } else {
        Object.assign(acc, flattenErrorKeys(formErrors[k], pre + k))
      }
    } else {
      acc[pre + k] = formErrors[k]
    }
    return acc
  }, {})

const composeErrorMessage = (errors) => {
  if (typeof errors === 'string') return errors

  if (Array.isArray(errors)) {
    return errors.join(' - ')
  }

  if (typeof errors === 'object') {
    return Object.keys((k) => `${k}: ${errors[k]}`).join(' ')
  }

  return JSON.stringify(errors)
}

export const setLogicFormErrors = (
  errors = {},
  submittedValues = {},
  identifierToIndexMap = {},
  setFieldError = () => {}
) => {
  if (errors.response && errors.response.status >= 500) {
    notify(networkError, 'error')
    return
  }

  if (errors.form_errors) {
    let formErrors = { logic: [] }

    errors.form_errors.logic?.forEach((errorObj, submittedLogicIndex) => {
      const fieldIdentifier =
        submittedValues.logic[submittedLogicIndex].identifier

      formErrors.logic[identifierToIndexMap[fieldIdentifier]] = errorObj
    })

    const flatErrors = flattenErrorKeys(formErrors)

    Object.keys(flatErrors).forEach((fieldName, index) => {
      if (!isEmpty(flatErrors[fieldName])) {
        setFieldError(fieldName, {
          message: composeErrorMessage(flatErrors[fieldName]),
        })
      }
    })

    showErrorNotification(getRawMessage('general.error.fixFormErrors'))
  }
}

export function debounce(func, wait, immediate) {
  var timeout
  return function () {
    var context = this,
      args = arguments
    var later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    var callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export const replaceObjKey = (replacements) => (obj) =>
  Object.keys(obj)
    .map((key) => {
      const newKey = replacements[key] || key
      return { [newKey]: obj[key] }
    })
    .reduce((acc, obj) => ({ ...acc, ...obj }), {})

export const mutativeRemoveFromArray = (array, index) => {
  if (isObservableArray(array) || Array.isArray(array)) {
    array.splice(index, 1)
  } else {
    throw Error(
      `This function expects an array or observableArray as first argument but it was called with (${array}, ${index})`
    )
  }
}

export const hasExactSameValues = (arrayA, arrayB) => {
  if (!Array.isArray(arrayA) || !Array.isArray(arrayB)) {
    throw new Error(
      `hasExactSameValues expects two arrays but received ${typeof arrayA} and ${typeof arrayB}`
    )
  }

  if (arrayA.length === arrayB.length) {
    for (let index = 0; index < arrayA.length; index++) {
      if (arrayA[index] === arrayB[index]) {
        return false
      }
    }

    return true
  } else {
    return false
  }
}

export function logFormData(formData) {
  if (process.env.NODE_ENV === 'development') {
    for (let pair of formData.entries()) {
      console.log(pair[0] + '  =>  ' + pair[1])
    }
  }
}

export const getElementDimensions = (element) => {
  if (element) {
    const { height, width } = element.getBoundingClientRect()
    return { width, height }
  }

  throw new TypeError(
    `getElementDimensions expects an HTML element,
       received ${typeof element}`
  )
}

export const omit = (blacklisted) => (obj) =>
  Object.entries(obj)
    .filter(([key]) => !blacklisted.includes(key))
    .reduce((obj, [key, val]) => Object.assign(obj, { [key]: val }), {})

export const filterObj = (whiteList) => (theObj) =>
  Object.keys(theObj)
    .filter((key) => whiteList.includes(key))
    .reduce((obj, key) => {
      obj[key] = theObj[key]
      return obj
    }, {})

export const filterUrlUnFriendlyChars = (value) => {
  if (value) {
    value = value.toString()

    const unAllowedCharsRegEx = /\/|\s|:|\||\\|#|%|\?|&|\$|@|=/gi

    return value.replace(unAllowedCharsRegEx, '')
  } else {
    return ''
  }
}

export const invalidateForm = (form, formErrors) => {
  form.each((field) => {
    if (formErrors[field.name]) {
      field.invalidate(formErrors[field.name])
      field.showErrors(true)
    }
  })

  form.showErrors(true)
}

export const checkResponseCatch = (fn, extractor) => (res) => {
  if (res.status >= 400) {
    fn(extractor(res.data.errors))
  }
}

export const truncate =
  (count = 30, ellipsis = true) =>
  (text = '') => {
    if (!text) return ''

    if (typeof text !== 'string') {
      throw new TypeError(
        `limitToChars expects a string as argument, received: ${typeof text}`
      )
    }

    const tail = ellipsis ? '...' : ''

    return text.length > count ? text.substr(0, count - 1) + tail : text
  }

export const isValidHTML = (htmlStr) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlStr, 'text/xml')
  if (doc.documentElement.querySelector('parsererror')) {
    return false
  } else {
    return true
  }
}

export const withFinally =
  (finalFn) =>
  (fn) =>
  (...args) => {
    fn(...args)
    finalFn()
  }

export const convertRgbaToString = ({ r, g, b, a } = {}) =>
  `rgba(${r}, ${g}, ${b}, ${a})`

export const addThemeCssTag = () => {
  const cssTagId = 'theme-css-tag'
  const cssTag = document.getElementById('theme-css-tag')

  if (!cssTag) {
    return addCssTag(cssTagId)
  } else {
    return cssTag.sheet
  }
}

const addCssTag = (id) => {
  const styleEl = document.createElement('style')
  document.head.appendChild(styleEl)
  return styleEl.sheet
}

export function isJson(str) {
  if (str && typeof str !== 'object') {
    try {
      JSON.parse(str)
      return true
    } catch (e) {
      return false
    }
  }
  return false
}

export const cleanObj = (obj, removeCandidates = [null, undefined]) => {
  return Object.keys(obj)
    .filter(function (k) {
      return !removeCandidates.includes(obj[k])
    })
    .reduce(function (acc, k) {
      acc[k] =
        typeof obj[k] === 'object' ? cleanObj(obj[k], removeCandidates) : obj[k]
      return acc
    }, {})
}

export const sortListByPosition = (list) =>
  list.sort((a, b) => a.position - b.position)

// use for validating Promise.all and Promise.allSettled response
export const validateResponse = ({ status, data }) => {
  if (resOK(status) && data) {
    return data.data
  } else {
    return { error: data && data.errors }
  }
}

export const isValidUrl = (link = '') => {
  const linkValidationRegex =
    /http(s?):\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/

  return linkValidationRegex.test(link)
}

/**
 * @param {UnknownArrayOrObject | boolean} dirtyFields
 * @param {UnknownArrayOrObject} allValues
 *
 * @returns UnknownArrayOrObject
 */

/** */
export const getDirtyFormFieldValues = (dirtyFields, allValues) => {
  // NOTE: Recursive function.

  if (!dirtyFields) return null

  // If *any* item in an array was modified, the entire array must be submitted, because there's no
  // way to indicate "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    return allValues
  }

  // Here, we have an object.
  return Object.fromEntries(
    Object.keys(dirtyFields).map((key) => [
      key,
      getDirtyFormFieldValues(dirtyFields[key], allValues[key]),
    ])
  )
}

export const isValidDateObject = (d) => {
  return d instanceof Date && !isNaN(d)
}

export const isValidDateString = (d) => {
  return typeof d === 'string' && !isNaN(new Date(d))
}

/**
 * @summary return numbers from start to end (inclusive) in an array.
 * in case end is less than start returns []
 *
 * @param {number} start
 * @param {number} end
 *
 * @returns {array}
 *
 */
export const getNumbersInRange = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, i) => Number(i + start))

/**
 *
 * @summary remove empty lines and trim white space from each line of string
 *
 * @param {string} str
 *
 * @returns {string}
 *
 */
export const removeAndTrimEmptyLines = (str) =>
  typeof str === 'string'
    ? str
        .split('\n')
        .filter(Boolean)
        .map((s) => s.trim())
        .join('\n')
    : ''

export const getDisplayFriendlyUrl = (url = '') => {
  try {
    // Decode the URL and remove the protocol and "www" if present
    return decodeURI(url).replace(/^(https?:\/\/)?(www\.)?/i, '')
  } catch (error) {
    console.error('Error decoding URL:', error)
    return url // Handle the error appropriately
  }
}
